import { quoteTrackingAllowed } from "./quote-conversion";

export const META_PIXEL_ID = "5396018373810381";

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};
type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

let initStarted = false;

function dispatch(args: unknown[]) {
  const fbq = (window as PixelWindow).fbq;
  if (!fbq) return;
  if (typeof fbq.callMethod === "function") fbq.callMethod.apply(fbq, args);
  else fbq.queue.push(args);
}

function installBaseSnippet() {
  const pixelWindow = window as PixelWindow;
  if (pixelWindow.fbq) return;
  const fbq = function (...args: unknown[]) {
    fbq.queue.push(args);
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  pixelWindow.fbq = fbq;
  pixelWindow._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
}

// Initializes once, only where the shared advertising permission allows it.
// Consent-required or unresolved regions stay blocked: no script, no events.
export async function initMetaPixel(): Promise<boolean> {
  if (initStarted) return true;
  if (!(await quoteTrackingAllowed())) return false;
  initStarted = true;
  installBaseSnippet();
  dispatch(["init", META_PIXEL_ID, {}, { agent: "lovable" }]);
  dispatch(["track", "PageView"]);
  return true;
}

// First call initializes (sending the initial PageView); later calls cover
// client-side navigation between pages.
export async function trackPageView(): Promise<void> {
  if (!initStarted) {
    await initMetaPixel();
    return;
  }
  dispatch(["track", "PageView"]);
}

// Fires the standard Lead event exactly once per successful submission,
// only after the backend confirmed success. Blocked submissions are consumed
// and never replayed after later consent changes.
export function createLeadReporter(isAllowed: () => Promise<boolean>, send: (eventId: string) => void) {
  const handled = new Set<string>();
  return async (submissionId: string, allowedAtSubmission: boolean): Promise<void> => {
    if (handled.has(submissionId)) return;
    handled.add(submissionId);
    if (!allowedAtSubmission || !(await isAllowed())) return;
    try { send(submissionId); } catch { /* Tracking must never interrupt contact delivery. */ }
  };
}

export const reportLeadSuccess = createLeadReporter(quoteTrackingAllowed, (eventId) => {
  if (!initStarted) return;
  dispatch(["track", "Lead", {}, { eventID: eventId }]);
});
